import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Briefcase,
  Building2,
  FileSpreadsheet,
  Gavel,
  Landmark,
  LayoutDashboard,
  LogOut,
  Menu,
  PieChart,
  ReceiptText,
  Settings as SettingsIcon,
  Users,
  Wallet,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { QuickSearch } from "@/components/QuickSearch";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: ReceiptText, testId: "nav-invoices" },
  { to: "/cases", label: "Matters", icon: Gavel, testId: "nav-cases" },
  { to: "/contacts", label: "Instructing Contacts", icon: Users, testId: "nav-contacts" },
  { to: "/firms", label: "Law Firms", icon: Building2, testId: "nav-firms" },
  { to: "/clients", label: "Clients", icon: Briefcase, testId: "nav-clients" },
  { to: "/reports", label: "Ledger & Aging", icon: PieChart, testId: "nav-reports" },
  { to: "/running-accounts", label: "Running Accounts", icon: Wallet, testId: "nav-running" },
  { to: "/tds", label: "TDS Register", icon: Landmark, testId: "nav-tds" },
  { to: "/statements", label: "Statements", icon: FileSpreadsheet, testId: "nav-statements" },
  { to: "/settings", label: "Chambers Settings", icon: SettingsIcon, testId: "nav-settings" },
];

const NavList = ({ onNavigate, isOwner = true }) => (
  <nav className="flex flex-col gap-0.5 px-3">
    {NAV.filter((item) => isOwner || item.to !== "/settings").map(({ to, label, icon: Icon, testId }) => (
      <NavLink
        key={to}
        to={to}
        data-testid={testId}
        onClick={onNavigate}
        className={({ isActive }) =>
          `group flex items-center gap-3 border-l-2 px-3 py-2.5 text-[13px] font-medium transition-colors duration-150 ${
            isActive
              ? "border-brand-terracotta bg-slate-900/[0.04] text-slate-900"
              : "border-transparent text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
          }`
        }
      >
        <Icon className="h-4 w-4" strokeWidth={1.6} />
        {label}
      </NavLink>
    ))}
  </nav>
);

const Brand = () => (
  <Link to="/dashboard" className="block px-6 pb-6 pt-7" data-testid="brand-link">
    <div className="flex items-center gap-3">
      <img src="/chambers-logo.png" alt="Chambers crest" className="h-10 w-10 object-contain" />
      <span className="font-serif text-lg leading-none text-slate-900">
        Chambers&rsquo; Billing
      </span>
    </div>
    <p className="label-caps mt-3">Chambers of Amir Arsiwala</p>
  </Link>
);

export default function AppLayout({ children }) {
  const { user, settings, logout } = useAuth();
  const isOwner = user?.role !== "clerk";
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const footer = (
    <div className="mt-auto border-t border-slate-200 px-5 py-4">
      <p className="truncate font-serif text-sm text-slate-900">
        {settings?.counsel_name || user?.name}
      </p>
      <p className="truncate text-[11px] text-slate-500">{user?.email}</p>
      {!isOwner && (
        <p
          className="mt-2 inline-block border border-slate-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500"
          data-testid="clerk-seat-chip"
        >
          Clerk seat
        </p>
      )}
      <button
        type="button"
        onClick={handleLogout}
        data-testid="logout-button"
        className="mt-3 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-red-600"
      >
        <LogOut className="h-3.5 w-3.5" strokeWidth={1.8} />
        Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF6F4]">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <Brand />
        <NavList isOwner={isOwner} />
        {footer}
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden no-print">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              data-testid="mobile-menu-button"
              className="flex h-9 w-9 items-center justify-center border border-slate-200 text-slate-700"
            >
              <Menu className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-72 flex-col p-0">
            <Brand />
            <NavList onNavigate={() => setMobileOpen(false)} isOwner={isOwner} />
            {footer}
          </SheetContent>
        </Sheet>
        <span className="font-serif text-base text-slate-900">Chambers&rsquo; Billing</span>
        <img src="/chambers-logo.png" alt="Chambers crest" className="h-8 w-8 object-contain" />
      </header>

      <InstallPrompt />

      <div className="no-print border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <QuickSearch testId="quick-search-mobile" />
      </div>

      <main className="lg:pl-64">
        <header className="no-print sticky top-0 z-20 hidden border-b border-slate-200 bg-[#FBF6F4]/90 backdrop-blur lg:block">
          <div className="mx-auto flex w-full max-w-[1400px] items-center gap-6 px-12 py-3">
            <div className="w-full max-w-md">
              <QuickSearch shortcut />
            </div>
          </div>
        </header>
        <div className="mx-auto w-full max-w-[1400px] px-5 py-8 sm:px-8 lg:px-12 lg:py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
